import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import ConnectButton from './components/ConnectButton';
import TokenStats from './components/TokenStats';
import MyBalance from './components/MyBalance';
import StakingPanel from './components/StakingPanel';
import BondsPanel from './bonds/BondsPanel';
import BondsOverview from './components/BondsOverview';

function App() {
  const walletConnector = useRef(null);
  const [tab, setTab] = useState(window.location.hash === '#bonds' ? 'bonds' : 'overview');
  const [isConnected, setIsConnected] = useState(!!localStorage.getItem('connectedAccount'));

  useEffect(() => {
    const syncTab = () => setTab(window.location.hash === '#bonds' ? 'bonds' : 'overview');
    window.addEventListener('hashchange', syncTab);
    window.addEventListener('popstate', syncTab);
    return () => {
      window.removeEventListener('hashchange', syncTab);
      window.removeEventListener('popstate', syncTab);
    };
  }, []);
  const selectTab = id => {
    setTab(id);
    if (window.location.hash !== `#${id}`) window.history.pushState(null, '', `#${id}`);
  };
  const exploreBonds = event => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    selectTab('bonds');
    document.getElementById('portal-tab-bonds')?.focus();
  };

  const handleConnectionStatus = (status) => {
    setIsConnected(status);
  };

  return (
    <div className="App bg-gradient-to-br from-blue-50 via-white to-purple-50 min-h-screen py-6">
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Wallet Controls - Top Right */}
        <div className="flex justify-end mb-6">
          <ConnectButton ref={walletConnector} handleConnectionStatus={handleConnectionStatus} />
        </div>
        
        <nav className="portal-tabs" aria-label="Token portal sections" role="tablist">
          {[['overview','Overview & staking'],['bonds','ADBYTES bonds']].map(([id,label]) => (
            <button key={id} id={`portal-tab-${id}`} className={id === 'bonds' ? 'portal-bonds-tab' : undefined} role="tab" aria-controls="portal-panel"
              tabIndex={tab === id ? 0 : -1} aria-selected={tab === id}
              onClick={() => selectTab(id)}
              onKeyDown={event => {
                if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
                event.preventDefault();
                const next = event.key === 'Home' ? 'overview' : event.key === 'End' ? 'bonds' : id === 'overview' ? 'bonds' : 'overview';
                selectTab(next);
                document.getElementById(`portal-tab-${next}`)?.focus();
              }}>{label}{id === 'bonds' && <span className="portal-tab-badge" aria-hidden="true">New</span>}</button>
          ))}
        </nav>
        <div id="portal-panel" role="tabpanel" aria-labelledby={`portal-tab-${tab}`}>
        {tab === 'bonds' ? <BondsPanel onConnectWallet={() => walletConnector.current?.connect()} /> : <>
        {/* Hero Section */}
        <div className="text-center mb-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">ADBYTES</span> Token
          </h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            Powering a transparent and rewarding advertising platform on the blockchain
          </p>
        </div>

        {/* Wallet Section */}
        {isConnected ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <TokenStats />
              <MyBalance />
            </div>
            <div className="mb-12">
              <StakingPanel />
            </div>
          </>
        ) : (
          <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl shadow-xl p-8 text-white text-center mb-12">
            <div className="max-w-md mx-auto">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto mb-4 opacity-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
              </svg>
              <h3 className="text-2xl font-bold mb-3">Connect Your Wallet</h3>
              <p className="text-blue-100 mb-6">
                Access your ADBYTES balance, add tokens to your wallet, and trade on Uniswap
              </p>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-sm text-left space-y-2">
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-green-300" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                  <span>View your token balance</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-green-300" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                  <span>Add ADBYTES to your wallet</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-green-300" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                  <span>Trade on Uniswap</span>
                </div>
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-green-300" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                  <span>Stake ADBYTES and earn APY</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tokenomics Section */}
        <section className="mb-12" aria-labelledby="token-economics-title">
          <h3 id="token-economics-title" className="text-2xl font-bold text-gray-900 text-center mb-8">Token Economics</h3>
          
          <div className="grid auto-rows-fr grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {/* Supply Card */}
            <div className="bg-white rounded-xl shadow-md p-6 border-t-4 border-blue-500 hover:shadow-lg transition-shadow">
              <div className="text-blue-600 mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">1 Billion</div>
              <div className="text-sm font-semibold text-gray-700 mb-2">Max Supply</div>
              <div className="text-xs text-gray-600 leading-relaxed">Fixed maximum supply; market value can rise or fall</div>
            </div>

            {/* Blockchain Card */}
            <div className="bg-white rounded-xl shadow-md p-6 border-t-4 border-purple-500 hover:shadow-lg transition-shadow">
              <div className="text-purple-600 mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">Base</div>
              <div className="text-sm font-semibold text-gray-700 mb-2">Blockchain</div>
              <div className="text-xs text-gray-600 leading-relaxed">Lightning fast transactions with minimal gas fees</div>
            </div>

            {/* Rewards Card */}
            <div className="bg-white rounded-xl shadow-md p-6 border-t-4 border-green-500 hover:shadow-lg transition-shadow">
              <div className="text-green-600 mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                </svg>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">222</div>
              <div className="text-sm font-semibold text-gray-700 mb-2">Per 10 Minutes</div>
              <div className="text-xs text-gray-600 leading-relaxed">Distributed to publishers based on traffic share</div>
            </div>

            {/* Staking Card */}
            <div className="bg-white rounded-xl shadow-md p-6 border-t-4 border-indigo-500 hover:shadow-lg transition-shadow">
              <div className="text-indigo-600 mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">33,000</div>
              <div className="text-sm font-semibold text-gray-700 mb-2">Daily Staking Rewards</div>
              <div className="text-xs text-gray-600 leading-relaxed">Stake ADBYTES to earn more ADBYTES — harvest or compound anytime</div>
            </div>

            {/* Utility Card */}
            <div className="bg-white rounded-xl shadow-md p-6 border-t-4 border-orange-500 hover:shadow-lg transition-shadow">
              <div className="text-orange-600 mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">Utility</div>
              <div className="text-sm font-semibold text-gray-700 mb-2">Redeem for Ad Credits</div>
              <div className="text-xs text-gray-600 leading-relaxed">Stable redemption value, burned to reduce supply</div>
            </div>
            <BondsOverview onExplore={exploreBonds} />
          </div>

          {/* Staking explainer */}
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl shadow-md p-6 sm:p-8 border border-indigo-100 mb-8">
            <h4 className="text-lg font-bold text-gray-900 mb-4">Staking</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-gray-600">
              <div>
                <div className="font-semibold text-gray-900 mb-1">1. Stake ADBYTES</div>
                <p>Deposit ADBYTES into the staking vault. You receive stADBYTES as a receipt for your staked position — a transferable token you can hold or move while your ADBYTES earns rewards.</p>
              </div>
              <div>
                <div className="font-semibold text-gray-900 mb-1">2. Earn ADBYTES Rewards</div>
                <p>Rewards accrue daily in ADBYTES based on your share of the total staked pool. The current emission rate is 33,000 ADBYTES per day, shared proportionally among all stakers.</p>
              </div>
              <div>
                <div className="font-semibold text-gray-900 mb-1">3. Harvest, Compound, or Unstake</div>
                <p>Harvest rewards to your wallet, restake to compound your position, or request an unstake with a cooldown period. APY adjusts as more tokens are staked.</p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">How ADBYTES Works</h3>
          <p className="text-sm text-gray-500 text-center mb-8 max-w-2xl mx-auto">
            Multiple ways to earn on the platform — from running ads to staking your tokens.
          </p>

          <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4 text-center">
            Ways to Earn
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
            <div className="rounded-xl border border-gray-100 bg-gray-50 p-5">
              <div className="bg-gradient-to-br from-green-400 to-green-600 w-10 h-10 rounded-lg flex items-center justify-center mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h5 className="font-semibold text-gray-900 mb-1">Publishers</h5>
              <p className="text-sm text-gray-600">
                Earn ADBYTES for verified ad banner traffic on your website. Rewards are distributed every 10 minutes based on your share of network traffic.
              </p>
            </div>

            <div className="rounded-xl border border-gray-100 bg-gray-50 p-5">
              <div className="bg-gradient-to-br from-blue-400 to-blue-600 w-10 h-10 rounded-lg flex items-center justify-center mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                </svg>
              </div>
              <h5 className="font-semibold text-gray-900 mb-1">Advertisers</h5>
              <p className="text-sm text-gray-600">
                Earn bonus ADBYTES when you purchase advertising credit on the platform to run your campaigns.
              </p>
            </div>

            <div className="rounded-xl border border-gray-100 bg-gray-50 p-5">
              <div className="bg-gradient-to-br from-purple-400 to-purple-600 w-10 h-10 rounded-lg flex items-center justify-center mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h5 className="font-semibold text-gray-900 mb-1">Affiliates</h5>
              <p className="text-sm text-gray-600">
                Earn 10% of all tokens withdrawn or claimed by users you refer to the platform.
              </p>
            </div>

            <div className="rounded-xl border border-gray-100 bg-gray-50 p-5">
              <div className="bg-gradient-to-br from-orange-400 to-orange-600 w-10 h-10 rounded-lg flex items-center justify-center mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <h5 className="font-semibold text-gray-900 mb-1">Paid to Click</h5>
              <p className="text-sm text-gray-600">
                Visit sponsored websites through the PTC program and earn ADBYTES for your engagement.
              </p>
            </div>

            <div className="rounded-xl border border-gray-100 bg-gray-50 p-5 sm:col-span-2 lg:col-span-2">
              <div className="bg-gradient-to-br from-pink-400 to-rose-600 w-10 h-10 rounded-lg flex items-center justify-center mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                </svg>
              </div>
              <h5 className="font-semibold text-gray-900 mb-1">Community Rewards</h5>
              <p className="text-sm text-gray-600">
                Participate in airdrops, bonus campaigns, and special community giveaways run by the platform.
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-100 p-6 mb-8">
            <div className="flex flex-col sm:flex-row sm:items-start gap-4">
              <div className="bg-gradient-to-br from-indigo-400 to-indigo-600 w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-1">Stake & Earn</h4>
                <p className="text-sm text-gray-600">
                  Lock your ADBYTES in the staking vault to earn daily ADBYTES rewards proportional to your share of the pool. Harvest rewards to your wallet or restake to compound your position over time.
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-blue-100 bg-blue-50 p-6">
            <div className="flex flex-col sm:flex-row sm:items-start gap-4">
              <div className="bg-gradient-to-br from-blue-400 to-blue-600 w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-1">Redeem & Burn</h4>
                <p className="text-sm text-gray-600">
                  Exchange your ADBYTES for advertising credit at a fixed value regardless of market price. All redeemed tokens are permanently burned, reducing circulating supply.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Distribution */}
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl shadow-lg p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Token Distribution</h3>
          
          <div className="max-w-3xl mx-auto">
            <div className="space-y-4">
              {/* Platform Rewards */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold text-gray-700">Platform Rewards</span>
                  <span className="text-sm font-bold text-gray-900">50%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div className="bg-gradient-to-r from-green-400 to-green-600 h-3 rounded-full" style={{width: '50%'}}></div>
                </div>
                <p className="text-xs text-gray-500 mt-1">Publishers, advertisers, affiliates & engagement rewards</p>
              </div>

              {/* Staking & Yield */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold text-gray-700">Staking & Yield Farming</span>
                  <span className="text-sm font-bold text-gray-900">15%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div className="bg-gradient-to-r from-blue-400 to-blue-600 h-3 rounded-full" style={{width: '15%'}}></div>
                </div>
                <p className="text-xs text-gray-500 mt-1">Rewards for stakers who hold and earn on the network</p>
              </div>

              {/* Token Sales */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold text-gray-700">Token Sales</span>
                  <span className="text-sm font-bold text-gray-900">20%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div className="bg-gradient-to-r from-purple-400 to-purple-600 h-3 rounded-full" style={{width: '20%'}}></div>
                </div>
              </div>

              {/* Platform/Team */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold text-gray-700">Platform/Team</span>
                  <span className="text-sm font-bold text-gray-900">10%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div className="bg-gradient-to-r from-gray-400 to-gray-600 h-3 rounded-full" style={{width: '10%'}}></div>
                </div>
              </div>

              {/* Airdrops */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold text-gray-700">Airdrops & Giveaways</span>
                  <span className="text-sm font-bold text-gray-900">5%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div className="bg-gradient-to-r from-orange-400 to-orange-600 h-3 rounded-full" style={{width: '5%'}}></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        </>}
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-16">
      </footer>
    </div>
  );
}

export default App;
